import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@economic/taco';
import { refreshCurrentLegacyPage } from '@economic/react-app';
import { ResendInvoiceDialog } from './ResendInvoiceDialog';
import { SendByType } from '../../invoices/book-and-send/types';
import { splitEmails } from '../../invoices/utils';
import { t, useLocalization } from '../../../../../shared/components/l10n/l10n';
import { usePostQuery } from '../../../../../shared/hooks/usePostQuery';
import {
    ResendInvoiceInformationResponse,
    InvoiceResendEmailOptionsRequest,
    ResendInvoiceElectronicallyRequest,
    EmailTemplate,
} from '../../../../../../types/internal-api';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import { InvoiceLayoutType } from '../../../../../../types/internal-api-extra';
import { useEmailTemplates } from '../../invoices/utils/useEmailTemplates';
import { emailTemplateFormState } from '../../invoices/utils/emailTemplateFormState';
import { handleError } from '../../shared/utils';

export interface ResendInvoiceFormState {
    sendByType: SendByType;
    invoiceId: number;
    isCreditNote: boolean;
    canBeSentElectronically: boolean;
    hasBeenSentElectronically: boolean;
    emailOptions: {
        subject: string;
        body: string;
        recipients: string;
        recipientsCc: string;
        recipientsBcc: string;
        encrypt: boolean;
        includePdfLink: boolean;
        includePdfAsAttachment: boolean;
        includeOioLink: boolean;
        includeOioAsAttachment: boolean;
    };
    eInvoiceOptions?: {
        sendingMethod: number;
        gln?: string;
        cvr?: string;
        pNumber?: string;
        yourRef?: number;
        attentionRef?: number;
        customerNumber: number;
    };
    templates: EmailTemplate[];
}

/**
 * Fetches data from two separate endpoints depending on whether or not we are resending electronically or not.
 *
 * When the original invoice was sent electronically we have oioxmlId > 0.
 *
 * @param {number} logNumber
 * @param {number} oioxmlId
 */
function useResendData(logNumber: number, oioxmlId: number) {
    const wasSentElectronically = oioxmlId !== 0;
    const shouldQueryAsEmail = !wasSentElectronically && logNumber !== 0;

    const queryMail = usePostQuery<ResendInvoiceInformationResponse>(
        `sales/invoice/ResendEmailInformation?logId=${logNumber}`,
        {
            logId: logNumber,
        },
        !shouldQueryAsEmail
    );
    const queryElectronic = usePostQuery<ResendInvoiceInformationResponse>(
        `sales/invoice/ResendElectronicallyInformation`,
        {
            oioId: oioxmlId,
        },
        !wasSentElectronically
    );

    return {
        loading: wasSentElectronically ? queryElectronic.loading : shouldQueryAsEmail ? queryMail.loading : true,
        data: wasSentElectronically ? queryElectronic.data : queryMail.data,
        error: wasSentElectronically ? queryElectronic.error : queryMail.error,
    };
}

async function resendEmail(request: InvoiceResendEmailOptionsRequest): Promise<any> {
    return await internalApiClient.post(`/sales/invoice/ResendEmail`, request);
}
async function resendElectronically(request: ResendInvoiceElectronicallyRequest): Promise<any> {
    return await internalApiClient.post(`/sales/invoice/ResendElectronically`, request);
}

function ResendInvoiceDialogContainer() {
    const navigate = useNavigate();
    const location = useLocation();
    const { logNumber, oioxmlId } = location.state || ({} as any);

    const loadingTexts = useLocalization(textIds);
    const toast = useToast();

    const [sending, setSending] = useState<boolean>(false);

    const { loading: dataLoading, data } = useResendData(logNumber, oioxmlId);

    const emailTemplateType = data ? (data.isCreditNote ? InvoiceLayoutType.CreditNote : InvoiceLayoutType.Invoice) : undefined;
    const { data: templates, loading: templatesLoading } = useEmailTemplates(emailTemplateType);

    const normalizedData: ResendInvoiceFormState | undefined = useMemo(() => {
        if (data) {
            return {
                ...data,
                sendByType: data.canBeSentElectronically ? SendByType.oioxml : SendByType.email,
                eInvoiceOptions: {
                    ...data.eInvoiceOptions,
                    sendingMethod: data.eInvoiceOptions.sendingMethod,
                    cvr: data.eInvoiceOptions.cvr,
                    gln: data.eInvoiceOptions.gln,
                    pNumber: data.eInvoiceOptions.pNumber,
                    yourRef: data.eInvoiceOptions.yourRef,
                    attentionRef: data.eInvoiceOptions.attentionRef,
                    customerNumber: data.eInvoiceOptions.customerNumber,
                    validCustomerContactIds: data.validCustomerContacts?.map(cc => cc.userInterfaceId) || [],
                },
                emailOptions: {
                    ...data.emailOptions,
                    recipients: data.emailOptions.recipients.join(' '),
                    recipientsCc: data.emailOptions.recipientsCc.join(' '),
                    recipientsBcc: data.emailOptions.recipientsBcc.join(' '),
                },
                initialEmailTemplate: {
                    subject: data.emailOptions.subject,
                    body: data.emailOptions.body,
                },
                ...emailTemplateFormState(templates, false),
            };
        }
    }, [data, templates]);

    async function send(values: any) {
        setSending(true);
        const success = true;
        try {
            if (values.sendByType === SendByType.email) {
                await resendEmail({
                    invoiceId: values.invoiceId,
                    ...values.emailOptions,
                    recipients: splitEmails(values.emailOptions.recipients),
                    recipientsCc: splitEmails(values.emailOptions.recipientsCc),
                    recipientsBcc: splitEmails(values.emailOptions.recipientsBcc),
                });
            } else if (values.sendByType === SendByType.oioxml) {
                const options = values.eInvoiceOptions;
                await resendElectronically({
                    invoiceId: values.invoiceId,
                    eInvoiceOptions: options,
                } as ResendInvoiceElectronicallyRequest);
            }
            if (success) {
                setSending(false);
                toast.success(t`TOAST_INVOICE_SEND_AGAIN_SUCCESS`);

                refreshContentFrameAfterSuccess();
                hide();
            } else {
                setSending(false);
            }
        } catch (error) {
            handleError(error, toast);
            setSending(false);
        }
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname);
    }

    if (!logNumber) {
        return null;
    }

    return (
        <ResendInvoiceDialog
            loading={dataLoading || templatesLoading || sending || loadingTexts}
            data={normalizedData}
            open={location.hash === '#resend-invoice'}
            onHide={hide}
            onSend={send}
        />
    );
}

export default ResendInvoiceDialogContainer;

const textIds = [
    'LOADING_TEXT1',
    'LOADING_TEXT2',
    'LOADING_TEXT3',
    'FORM_RESEND_INVOICE_HEADER',
    'FORM_RESEND_SEND_BY_EMAIL',
    'FORM_RESEND_SEND_BY_EINVOICE',
    'FORM_RESEND_BUTTON_CANCEL',
    'FORM_RESEND_BUTTON_SEND',
    'TOAST_INVOICE_SEND_AGAIN_SUCCESS',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'FORM_RESEND_ERRORS_CANNOTRESEND',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OIOXML_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OIOXML_LINK',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    'SHARED_FORM_OK',
    'POPUP_INVOICEETC_ERROR_INVALIDEMAIL',
    'POPUP_INVOICEETC_RESENDCREDITNOTE',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'ERROR_CODE_CANNOTRESENDINVOICEPDFGENERATIONQUEUED',
];
